import { useMemo, useState } from 'react';
import type { TargetProbeLegRecord } from '@nadoutong/contracts';
import { useGuaranteedServiceList } from '../../api/guaranteedService.ts';
import { useTargetProbeLegs } from '../../api/useTargetProbes.ts';
import { useT } from '../../i18n/index.ts';
import { Checkbox, Chip, Input, Panel, Table, TBody, Td, Th, THead, Tr } from '../../ui/index.ts';
import { LegRow } from './LegRow.tsx';
import { legReachable } from './legLocations.ts';
import {
  groupLegs,
  LEG_UNIQUE,
  legFaceKey,
  legLookalikes,
  legIsAbnormal,
  legIsManual,
  legLamp,
  legMatchesQuery,
  type LegLamp,
  type LegSkuArming,
} from './legModel.ts';
import { LEG_SKU_ALL_OFF, legSkuArmingIndex } from './skuArming.ts';

function toggleSet(current: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(current);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

function countWhere(lamps: readonly LegLamp[], wanted: LegLamp): number {
  return lamps.filter((lamp) => lamp === wanted).length;
}

export function LegStatusCard({ nowMs = Date.now() }: { readonly nowMs?: number }): JSX.Element {
  const t = useT();
  const legsQuery = useTargetProbeLegs();
  const list = useGuaranteedServiceList();
  const [query, setQuery] = useState('');
  const [abnormalOverride, setAbnormalOverride] = useState<boolean | null>(null);
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const [unreachableOpen, setUnreachableOpen] = useState(false);
  const configured = useMemo(() => legSkuArmingIndex(list.data?.sku ?? []), [list.data]);
  // Configured SKUs show every leg; any other SKU shows only the legs an
  // operator removed by hand, so they stay visible (and probe-able) for
  // maintenance instead of vanishing from the one place that lists legs.
  const configuredLegs = (legsQuery.data?.legs ?? [])
    .filter((leg) => configured.has(leg.sku_id) || legIsManual(leg));
  // A leg whose every target sits where no request reaches (a config with no
  // customers, a shadowed SKU default) takes no traffic and is not probed on
  // schedule: it stays out of the counts and behind the bottom toggle.
  const legs = configuredLegs.filter(legReachable);
  const lamps = legs.map((leg) => legLamp(leg, nowMs));
  const anyAbnormal = lamps.some((lamp) => legIsAbnormal(lamp));
  const abnormalOnly = abnormalOverride ?? anyAbnormal;
  const matches = (leg: TargetProbeLegRecord): boolean => {
    if (!legMatchesQuery(leg, query)) return false;
    if (abnormalOnly && !legIsAbnormal(legLamp(leg, nowMs))) return false;
    return true;
  };
  const groups = groupLegs(legs.filter(matches));
  const unreachableShown = configuredLegs.filter((leg) => !legReachable(leg)).filter(matches);
  const unreachableGroups = unreachableOpen ? groupLegs(unreachableShown) : [];
  const refreshed = legsQuery.dataUpdatedAt > 0
    ? Math.max(0, Math.floor((Date.now() - legsQuery.dataUpdatedAt) / 1000))
    : 0;

  return (
    <Panel
      testId="guaranteed-target-probe-card"
      caption={(
        <span className="inline-flex items-baseline gap-2">
          <span>{t('guaranteed.section.legs')}</span>
          <span className="text-label font-normal text-paper-low">
            {t('guaranteed.leg.scope', { count: String(legs.length) })}
          </span>
        </span>
      )}
      actions={(
        <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
          <Chip tone="ok" lamp={false}>{t('guaranteed.leg.count.ok', { count: String(countWhere(lamps, 'ok')) })}</Chip>
          <Chip tone="warn" lamp={false}>{t('guaranteed.leg.count.shadow', { count: String(countWhere(lamps, 'shadow')) })}</Chip>
          <Chip tone="warn" lamp={false}>{t('guaranteed.leg.count.failing', { count: String(countWhere(lamps, 'failing')) })}</Chip>
          <Chip tone="bad" lamp={false}>{t('guaranteed.leg.count.tripped', { count: String(countWhere(lamps, 'tripped')) })}</Chip>
          <Chip tone="neutral" lamp={false}>{t('guaranteed.leg.count.manual', { count: String(countWhere(lamps, 'manual')) })}</Chip>
          <Chip tone="signal" lamp={false}>{t('guaranteed.leg.count.immune', { count: String(countWhere(lamps, 'immune')) })}</Chip>
        </span>
      )}
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-line-soft px-3 py-2">
        <label className="flex items-center gap-2 text-label text-paper-mid">
          <span>{t('guaranteed.leg.search')}</span>
          <Input
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={t('guaranteed.leg.search_placeholder')}
            aria-label={t('guaranteed.leg.search')}
            wrapperClassName="w-56"
            data-testid="guaranteed-leg-search"
          />
        </label>
        <Checkbox
          checked={abnormalOnly}
          onCheckedChange={setAbnormalOverride}
          label={t('guaranteed.leg.abnormal')}
          testId="guaranteed-leg-abnormal"
        />
        <span className="text-label text-paper-low">
          {t('guaranteed.leg.refreshed', { seconds: String(refreshed) })}
        </span>
      </div>
      {configuredLegs.length === 0 ? (
        <p className="m-0 px-3 py-3 text-dense text-paper-low">{t('guaranteed.leg.empty')}</p>
      ) : (
        <Table className="rounded-none border-0 bg-transparent" tableClassName="w-full min-w-[1040px] table-fixed">
          <THead>
            <Tr interactive={false}>
              <Th className="w-[168px] whitespace-nowrap">{t('guaranteed.leg.col.status')}</Th>
              <Th className="w-[210px] whitespace-nowrap">{t('guaranteed.leg.col.upstream')}</Th>
              <Th>{t('guaranteed.leg.col.evidence')}</Th>
              <Th className="w-[168px] whitespace-nowrap">{t('guaranteed.leg.col.last')}</Th>
              <Th className="w-[88px] whitespace-nowrap">{t('guaranteed.leg.col.next')}</Th>
              <Th num className="w-[56px] whitespace-nowrap">{t('guaranteed.leg.col.backoff')}</Th>
              <Th num className="w-[112px] whitespace-nowrap">{t('guaranteed.leg.col.members')}</Th>
              <Th className="w-[132px] whitespace-nowrap">{t('guaranteed.leg.col.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {groups.map((namespace) => {
              const namespaceKey = namespace.namespace === '' ? 'other' : namespace.namespace;
              const namespaceOpen = !closed.has(`ns:${namespaceKey}`);
              const label = namespace.namespace === ''
                ? t('guaranteed.leg.namespace_other')
                : namespace.namespace;
              return (
                <NamespaceBlock
                  key={namespaceKey}
                  namespaceKey={namespaceKey}
                  label={label}
                  open={namespaceOpen}
                  skus={namespace.skus}
                  closed={closed}
                  nowMs={nowMs}
                  arming={configured}
                  onToggle={(token) => setClosed((current) => toggleSet(current, token))}
                />
              );
            })}
            {unreachableShown.length === 0 ? null : (
              <Tr interactive={false} data-testid="guaranteed-leg-unreachable-toggle-row">
                <Td colSpan={8} className="py-1.5 text-label text-paper-low">
                  <button
                    type="button"
                    aria-expanded={unreachableOpen}
                    onClick={() => setUnreachableOpen((open) => !open)}
                    className="hover:text-paper"
                    data-testid="guaranteed-leg-unreachable-toggle"
                  >
                    {t('guaranteed.leg.unreachable_toggle', { count: String(unreachableShown.length) })}
                    {' '}
                    {unreachableOpen ? '▾' : '▸'}
                  </button>
                </Td>
              </Tr>
            )}
            {unreachableGroups.map((namespace) => {
              const namespaceKey = `unreachable:${namespace.namespace === '' ? 'other' : namespace.namespace}`;
              return (
                <NamespaceBlock
                  key={namespaceKey}
                  namespaceKey={namespaceKey}
                  label={namespace.namespace === '' ? t('guaranteed.leg.namespace_other') : namespace.namespace}
                  open={!closed.has(`ns:${namespaceKey}`)}
                  skus={namespace.skus}
                  closed={closed}
                  nowMs={nowMs}
                  arming={configured}
                  onToggle={(token) => setClosed((current) => toggleSet(current, token))}
                />
              );
            })}
          </TBody>
        </Table>
      )}
      <p className="m-0 px-3 py-2 text-micro text-paper-low" data-testid="guaranteed-leg-legend">
        {t('guaranteed.leg.legend')}
      </p>
    </Panel>
  );
}

function NamespaceBlock({
  namespaceKey,
  label,
  open,
  skus,
  closed,
  nowMs,
  arming,
  onToggle,
}: {
  readonly namespaceKey: string;
  readonly label: string;
  readonly open: boolean;
  readonly skus: readonly { readonly skuId: string; readonly skuName: string; readonly legs: readonly TargetProbeLegRecord[] }[];
  readonly closed: ReadonlySet<string>;
  readonly nowMs: number;
  readonly arming: ReadonlyMap<string, LegSkuArming>;
  readonly onToggle: (token: string) => void;
}): JSX.Element {
  return (
    <>
      <Tr interactive={false} data-testid={`guaranteed-leg-namespace-${namespaceKey}`}>
        <Td colSpan={8} className="bg-raised/40 py-1 font-medium text-paper-mid">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => onToggle(`ns:${namespaceKey}`)}
          >
            {open ? '▾' : '▸'} {label}
          </button>
        </Td>
      </Tr>
      {open ? skus.map((sku) => {
        const skuOpen = !closed.has(`sku:${sku.skuId}`);
        return (
          <SkuBlock
            key={sku.skuId}
            skuId={sku.skuId}
            skuName={sku.skuName}
            open={skuOpen}
            legs={sku.legs}
            nowMs={nowMs}
            arming={arming.get(sku.skuId) ?? LEG_SKU_ALL_OFF}
            configured={arming.has(sku.skuId)}
            onToggle={() => onToggle(`sku:${sku.skuId}`)}
          />
        );
      }) : null}
    </>
  );
}

function SkuBlock({
  skuId,
  skuName,
  open,
  legs,
  nowMs,
  arming,
  configured,
  onToggle,
}: {
  readonly skuId: string;
  readonly skuName: string;
  readonly open: boolean;
  readonly legs: readonly TargetProbeLegRecord[];
  readonly nowMs: number;
  readonly arming: LegSkuArming;
  /** False: the SKU has no guaranteed-service row; only its manual legs are listed. */
  readonly configured: boolean;
  readonly onToggle: () => void;
}): JSX.Element {
  const t = useT();
  const lookalikes = legLookalikes(legs);
  return (
    <>
      <Tr interactive={false} data-testid={`guaranteed-leg-sku-${skuId}`}>
        <Td colSpan={8} className="bg-card py-1 pl-6 font-medium text-paper">
          <span className="inline-flex items-center gap-2">
            <button type="button" aria-expanded={open} onClick={onToggle}>
              {open ? '▾' : '▸'} {skuName}
            </button>
            {configured ? null : (
              <span title={t('guaranteed.leg.unconfigured_tip')}>
                <Chip tone="neutral" lamp={false} testId={`guaranteed-leg-sku-unconfigured-${skuId}`}>
                  {t('guaranteed.leg.unconfigured')}
                </Chip>
              </span>
            )}
          </span>
        </Td>
      </Tr>
      {open ? legs.map((leg) => (
        <LegRow
          key={`${leg.upstream_id}:${leg.account_id}:${leg.key_bucket_id}:${leg.upstream_channel_id}:${leg.physical_model}:${leg.region}`}
          leg={leg}
          nowMs={nowMs}
          arming={arming}
          lookalike={lookalikes.get(legFaceKey(leg)) ?? LEG_UNIQUE}
        />
      )) : null}
    </>
  );
}
